// src/components/ui/globe/GlobalInsights.tsx

// This component renders a visually rich section featuring an interactive/static globe
// with descriptive text and features laid out in a grid below

"use client"

import { GradientSteelPurple, GradientLightNavy } from "~/components/gradients"
import { useInView } from 'react-intersection-observer'
import { type FunctionComponent, useState, useEffect } from "react"
import { StaticGlobe } from "~/components/ui/globe/GlobeStatic"
import { globeFeatures } from "~/components/ui/globe/globeFeatures"
import dynamic from 'next/dynamic'

// Dynamically import Globe component with fallback to static version
// This helps with initial page load performance by deferring the heavier interactive globe
const DynamicGlobe = dynamic(() => import('~/components/ui/globe/Globe').then(mod => mod.Globe), {
  ssr: false,
  loading: () => <StaticGlobe />,
})

interface GlobalInsightsProps {
  shouldShowInteractive: boolean // Controls whether to show interactive or static globe
  key?: string
}

export const GlobalInsights: FunctionComponent<GlobalInsightsProps> = ({
  shouldShowInteractive
}) => {
  // Defer intersection observer setup until after initial hydration
  // This reduces Total Blocking Time (TBT) by avoiding synchronous observer initialization
  const [enableInView, setEnableInView] = useState(false)
  
  useEffect((): void => {
    // Use requestIdleCallback if available, otherwise setTimeout as fallback
    const scheduleObserver: (callback: () => void) => void = (callback: () => void) => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 2000 })
      } else {
        setTimeout(callback, 100)
      }
    }

    scheduleObserver((): void => setEnableInView(true))
  }, [])

  // Setup intersection observer to track when section is in view
  // CRITICAL ANIMATION BEHAVIOR:
  // 
  // triggerOnce: true vs false tradeoffs:
  // 
  // triggerOnce: false (ORIGINAL BEHAVIOR):
  //   ✅ Globe only animates when visible (better performance when off-screen)
  //   ✅ Pauses animation when scrolled out of view (saves CPU/battery)
  //   ❌ Causes visual hitching during scroll in/out transitions
  //   ❌ Multiple intersection observer triggers on scroll cause re-renders
  //   ❌ Animation start/stop creates janky UX during scroll
  // 
  // triggerOnce: true (CURRENT BEHAVIOR):
  //   ✅ Eliminates scroll hitching by avoiding animation start/stop cycles  
  //   ✅ Smooth continuous animation once started
  //   ✅ No re-renders on scroll in/out of view
  //   ❌ Globe continues animating even when off-screen (slightly higher CPU usage)
  //   ❌ Animation never pauses once started until component unmounts
  // 
  // DECISION RATIONALE:
  // Chose triggerOnce: true because:
  // 1. User experience (smooth scrolling) trumps minor performance cost
  // 2. Globe is only rendered on homepage, so continuous animation impact is minimal
  // 3. Modern devices handle the animation load well
  // 4. Performance cost of off-screen animation < performance cost of scroll hitching
  //
  // EDGE CASES HANDLED:
  // - Component unmount still properly cleans up animation (see Globe.tsx)
  // - Navigation away from page stops animation via component lifecycle
  // - Animation FPS is capped at 60fps to prevent excessive CPU usage
  // - Low-performance devices get reduced animation complexity automatically
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true, // Start animation on first view, never stop
    delay: 200, // Debounce to prevent premature triggering during fast scrolls
    skip: !enableInView // Skip observer setup until after idle callback
  })

  return (
    <div className="px-2">
      {/* Main section container with shadow and rounded corners */}
      <section
        ref={ref}
        aria-labelledby="global-insights-title"
        className="z-10 relative mx-auto flex w-full max-w-6xl flex-col items-center justify-center overflow-hidden rounded-3xl pt-20 shadow-xl shadow-slate-900/50 dark:shadow-indigo-950/10 md:mt-20">
        {/* Background gradient overlay - provides the base color wash */}
        <div className="z-0 absolute inset-0" aria-hidden="true">
          <svg 
            className="h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <GradientSteelPurple id="GradientSteelPurple" />
            <GradientLightNavy id="GradientLightNavy" />
            <rect width="100%" height="100%" className="dark:fill-[url(#GradientSteelPurple)] fill-[url(#GradientLightNavy)]" />
          </svg>
        </div>

        {/* Subtitle pill with glowing border effect */}
        <div className="z-10 inline-block rounded-lg border border-indigo-400/20 bg-indigo-800/20 px-3 py-1.5 font-semibold uppercase leading-4 tracking-tight sm:text-sm">
          <span className="bg-gradient-to-b from-indigo-50 to-indigo-300 dark:bg-gradient-to-b dark:from-indigo-100 dark:to-indigo-400 bg-clip-text text-transparent">
            Made for performance marketers
          </span>
        </div>

        {/* Main title with gradient text effect */}
        <h2
          id="global-insights-title"
          className="z-10 mt-6 inline-block bg-gradient-to-b from-white to-indigo-50 bg-clip-text px-2 text-center text-5xl font-bold tracking-tighter text-transparent md:text-8xl">
          The global <br /> creative vantage
        </h2>

        {/* Globe container - renders either interactive or static version */}
        {shouldShowInteractive ? (
          <DynamicGlobe inView={inView} />
        ) : (
          <StaticGlobe />
        )}

        {/* Screen reader description */}
        <div className="sr-only" aria-live="polite">
          <p>Interactive visualization of a spinning globe representing AdSights&apos; global creative vantage, the platform&apos;s ability to provide unified insights and drive performance for marketers worldwide.</p>
        </div>

        {/* Features section container with gradient fade and positioning */}
        <div className="z-20 -mt-32 h-[38rem] w-full overflow-hidden md:-mt-36 sm:-mt-34">
          {/* Gradient overlay for text contrast */}
          <div className="z-0 absolute bottom-0 h-3/5 w-full bg-gradient-to-b from-transparent via-slate-950/70 to-gray-950 dark:bg-gradient-to-b dark:from-transparent dark:via-gray-950/95 dark:to-gray-950" aria-hidden="true" />
          
          {/* Features grid container with frosted glass effect */}
          <div className="absolute inset-x-6 bottom-12 m-auto max-w-4xl md:top-2/3">
            {/* Frosted glass panel with border - uses backdrop-blur and transparent background */}
            <div 
              className="grid grid-cols-1 gap-x-10 gap-y-6 rounded-lg border border-white/[7%] bg-white/[3%] dark:border dark:border-white/[3%] dark:bg-white/[1%] px-6 py-6 shadow-xl backdrop-blur md:grid-cols-3 md:p-8"
              role="list"
              aria-label="Key features"
            >
              {/* Map through features and render each with gradient text */}
              {globeFeatures.map((item) => (
                <div key={item.name} className="flex flex-col gap-2" role="listitem">
                  <h3 className="whitespace-nowrap bg-gradient-to-b from-white to-indigo-400 dark:bg-gradient-to-b dark:from-indigo-300 dark:to-indigo-400 bg-clip-text text-lg font-semibold text-transparent md:text-xl">
                    {item.name}
                  </h3>
                  <p className="text-sm leading-6 text-white" aria-label={`${item.name} description`}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
